import { Environment } from '@cobliteam/react-environments';
import decode, { type JwtPayload } from 'jwt-decode';
import { z } from 'zod/v4';

// As mesmas chaves que o login do api-lib grava, para as duas camadas enxergarem a mesma sessão.
const REFRESH_TOKEN_KEY = 'cobli-refresh-token';
const ACCESS_TOKEN_KEY = 'cobli-access-token';
const TEMPORARY_TOKEN_COOKIE = 'cobler-temporary-token';

// Folga para relógio do cliente atrasado: um token que ainda vale aqui já pode ter vencido no servidor.
const RENEWAL_BUFFER_SECONDS = 120;
const PRECONDITION_REQUIRED = 428;

const NO_SESSION = 'Sem sessão: não há token guardado.';

// Só os campos que a primitiva grava; usuário e frotas continuam com o api-lib.
const sessionSchema = z.object({
  session_id: z.string().min(1),
  authentication_token: z.string().nullish(),
});

type SessionResponse = z.infer<typeof sessionSchema>;

type DashboardWindow = Window & { __DASHBOARD_MONOREPO_VERSION__?: unknown };

// Separa `no-session`, que tentar de novo não resolve, das falhas da renovação, para o retry do app decidir.
export type SessionErrorReason = 'no-session' | 'network' | 'http' | 'invalid-body';

/** Falha de sessão. `status` é o HTTP da renovação, ausente quando não houve resposta. */
export class SessionError extends Error {
  override readonly name = 'SessionError';
  readonly reason: SessionErrorReason;
  readonly status: number | undefined;

  constructor(message: string, { reason, status, cause }: { reason: SessionErrorReason; status?: number; cause?: unknown }) {
    super(message, { cause });
    this.reason = reason;
    this.status = status;
  }
}

const readRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);
const readAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

/** Headers de autenticação com os tokens guardados agora, os mesmos que o api-lib manda. */
export const getAuthHeaders = (): Record<string, string> => {
  const accessToken = readAccessToken();
  const version = (window as DashboardWindow).__DASHBOARD_MONOREPO_VERSION__;
  return {
    'cobli-api-sid': readRefreshToken() ?? '',
    ...(version != null && { 'cobli-dashboard-version': String(version) }),
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };
};

const expiresSoon = (accessToken: string) => {
  try {
    const { exp } = decode<JwtPayload>(accessToken);
    return !exp || Date.now() >= (exp - RENEWAL_BUFFER_SECONDS) * 1000;
  } catch {
    return true;
  }
};

const requestSession = async (): Promise<SessionResponse> => {
  const url = `${Environment.getInstance().getEnvironment().herbieApiHost}/account/session`;
  let response: Response;
  try {
    response = await fetch(url, { credentials: 'include', headers: getAuthHeaders() });
  } catch (cause) {
    throw new SessionError('A renovação da sessão não chegou ao servidor.', { reason: 'network', cause });
  }
  // O 428 traz a sessão no corpo e vale como sucesso, como no SessionManager do api-lib.
  if (!response.ok && response.status !== PRECONDITION_REQUIRED) {
    throw new SessionError(`A renovação da sessão falhou com ${response.status}.`, { reason: 'http', status: response.status });
  }
  const body = sessionSchema.safeParse(await response.json().catch(() => undefined));
  if (!body.success) {
    throw new SessionError('A resposta de /account/session não trouxe session_id.', {
      reason: 'invalid-body',
      status: response.status,
      cause: body.error,
    });
  }
  return body.data;
};

const storeSession = ({ session_id, authentication_token }: SessionResponse) => {
  localStorage.setItem(REFRESH_TOKEN_KEY, session_id);
  if (authentication_token) localStorage.setItem(ACCESS_TOKEN_KEY, authentication_token);
};

// Sem path=/, o cookie gravado com path=/ só é apagado quando a URL atual tem um segmento.
const clearSession = () => {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  const domain = window.location.hostname.includes('localhost') ? 'localhost' : 'cobli.co';
  // biome-ignore lint/suspicious/noDocumentCookie: apaga o cookie que o TemporaryTokenManager do api-lib grava por document.cookie.
  document.cookie = `${TEMPORARY_TOKEN_COOKIE}=; max-age=0; path=/; domain=${domain}`;
};

// Os dois tokens: a impersonação do Cobler não tem refresh token, e a troca de frota mantém o mesmo.
type SessionTokens = { refreshToken: string | null; accessToken: string | null };

const readTokens = (): SessionTokens => ({ refreshToken: readRefreshToken(), accessToken: readAccessToken() });
const sameTokens = (a: SessionTokens, b: SessionTokens) => a.refreshToken === b.refreshToken && a.accessToken === b.accessToken;

// `discarded`: a sessão mudou durante o voo, então a atual não foi renovada por ele.
type RenewalOutcome = 'stored' | 'discarded';

const renewFor = async (tokens: SessionTokens): Promise<RenewalOutcome> => {
  const isCurrent = () => sameTokens(readTokens(), tokens);
  try {
    const session = await requestSession();
    if (!isCurrent()) return 'discarded';
    storeSession(session);
    return 'stored';
  } catch (error) {
    // A sessão mudou durante o voo: a falha era da anterior e não diz nada da atual.
    if (!isCurrent()) return 'discarded';
    if (error instanceof SessionError && (error.status === 401 || error.status === 403)) clearSession();
    throw error;
  }
};

// Voo único entre todos os chamadores, amarrado aos tokens que o iniciaram.
let renewal: { tokens: SessionTokens; promise: Promise<RenewalOutcome> } | null = null;

const renew = () => {
  const tokens = readTokens();
  if (renewal && sameTokens(renewal.tokens, tokens)) return renewal.promise;

  const entry = {
    tokens,
    promise: renewFor(tokens).finally(() => {
      if (renewal === entry) renewal = null;
    }),
  };
  renewal = entry;
  return entry.promise;
};

const noSession = () => new SessionError(NO_SESSION, { reason: 'no-session' });

const currentAccessToken = () => {
  const accessToken = readAccessToken();
  if (!accessToken) throw noSession();
  return accessToken;
};

const hasSession = () => Boolean(readAccessToken() || readRefreshToken());

const needsRenewal = () => {
  const accessToken = readAccessToken();
  return !accessToken || expiresSoon(accessToken);
};

/**
 * Access token com mais de 120 s de validade. Perto de vencer, ilegível ou ausente, a sessão é
 * renovada antes, num voo único compartilhado por todos os chamadores. Sem nenhum token, rejeita
 * sem ir à rede.
 */
// Um voo descartado não renovou a sessão que entrou no meio dele; ela é conferida uma vez, sem laço.
const renewAndCheckCurrent = async () => {
  if ((await renew()) === 'discarded' && needsRenewal()) await renew();
};

export const getAccessToken = async (): Promise<string> => {
  if (!hasSession()) throw noSession();
  if (needsRenewal()) await renewAndCheckCurrent();
  return currentAccessToken();
};

/**
 * Renova a sessão já, para quando o servidor recusa um token que ainda parecia válido. Se a conta
 * trocou durante o voo, o token recusado era o da anterior, e o da conta atual é devolvido se ainda vale.
 */
export const renewSession = async (): Promise<string> => {
  if (!hasSession()) throw noSession();
  await renewAndCheckCurrent();
  return currentAccessToken();
};
